












import SplitType from 'split-type';
import { gsap } from 'gsap';

function toScale(value, fallback, min, max) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, num));
}

export function initAniSplitStagger(options = {}) {
  const {
    
    targetSelector = '.ani-split-stagger',

    
    splitTypes = 'words,chars',

    
    fromVars = { opacity: 0, yPercent: 20 },
    toVars   = { opacity: 1, yPercent: 0, duration: 0.6, ease: 'power2.out' },
    stagger  = 0.02,

    
    reverseOnExit = true,
    resetOnExit   = false,

    
    once = false,

    
    respectReducedMotion = true,

    
    durationScale,
    delayScale,
    staggerScale
  } = options;

  const prefersReduced = respectReducedMotion &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const data = document.body?.dataset || {};
  const runtimeDurationScale = toScale(durationScale ?? data.aniDurationScale, 1, 0.25, 2);
  const runtimeDelayScale = toScale(delayScale ?? data.aniDelayScale, 1, 0, 2);
  const runtimeStaggerScale = toScale(staggerScale ?? data.aniSplitStaggerScale, 1, 0.25, 2);

  
  const timelines = new WeakMap();
  const splits = new WeakMap();
  const splitItems = new WeakMap();
  const hostReservations = new WeakMap();
  const splitTargets = new Set();
  const reservedHosts = new Set();
  const finalized = new WeakSet();
  const disposers = [];

  function readPx(value) {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : 0;
  }

  function layoutHostFor(el) {
    const ownDisplay = getComputedStyle(el).display;
    if (ownDisplay !== 'inline' && ownDisplay !== 'contents') return el;

    let host = el.parentElement;
    while (host && host !== document.body) {
      const display = getComputedStyle(host).display;
      if (display !== 'inline' && display !== 'contents') return host;
      host = host.parentElement;
    }

    return el;
  }

  function reserveSplitHeight(host, el, beforeHeight) {
    const hostHeight = host.getBoundingClientRect().height;
    const elHeight = el.getBoundingClientRect().height;
    const reserve = Math.ceil(Math.max(beforeHeight, hostHeight, elHeight));
    if (!reserve) return;

    let data = hostReservations.get(host);
    if (!data) {
      data = {
        minHeight: host.style.minHeight || '',
        reservedHeight: 0
      };
      hostReservations.set(host, data);
      reservedHosts.add(host);
    }

    const currentMinHeight = readPx(getComputedStyle(host).minHeight);
    const nextHeight = Math.ceil(Math.max(reserve, currentMinHeight, data.reservedHeight));
    data.reservedHeight = nextHeight;
    host.style.minHeight = `${nextHeight}px`;
  }

  const isOnceElement = (el) =>
    !!(once || el?.classList?.contains('ani-once') || el?.hasAttribute?.('data-ani-once'));

  const shouldRevertOnFinalize = (el) => el?.dataset?.aniSplitRevert === 'true';

  function finalizeOnce(el) {
    if (!(el instanceof Element)) return;
    if (finalized.has(el)) return;
    finalized.add(el);
    el.__aniSplitPlayedOnce = true;
    const tl = timelines.get(el);
    if (tl) {
      try { tl.kill(); } catch {}
      timelines.delete(el);
    }
    const split = splits.get(el);
    if (split && shouldRevertOnFinalize(el)) {
      try { split.revert(); } catch {}
      splits.delete(el);
      splitItems.delete(el);
      splitTargets.delete(el);
      return;
    }
    const items = splitItems.get(el);
    if (items?.length) {
      try { gsap.set(items, { opacity: 1, yPercent: 0 }); } catch {}
    }
  }

  function buildFor(el) {
    if (prefersReduced) return;
    if (finalized.has(el)) return;
    if (timelines.has(el)) return;

    const host = layoutHostFor(el);
    const beforeHeight = Math.max(
      host.getBoundingClientRect().height,
      el.getBoundingClientRect().height
    );
    const split = new SplitType(el, { types: splitTypes });
    reserveSplitHeight(host, el, beforeHeight);
    splits.set(el, split);
    splitTargets.add(el);

    const items =
      (split.chars?.length ? split.chars :
       split.words?.length ? split.words :
       split.lines?.length ? split.lines : [el]);
    splitItems.set(el, items);

    const baseDuration = Number(toVars?.duration);
    const scaledDuration = Number.isFinite(baseDuration)
      ? Math.max(0.12, baseDuration * runtimeDurationScale)
      : toVars?.duration;
    const baseStagger = Number(stagger);
    const scaledStagger = Number.isFinite(baseStagger)
      ? Math.max(0, baseStagger * runtimeStaggerScale)
      : stagger;
    const tl = gsap.timeline({
      paused: true,
      onComplete: () => {
        if (isOnceElement(el)) finalizeOnce(el);
      }
    });
    tl.fromTo(items, fromVars, { ...toVars, duration: scaledDuration, stagger: scaledStagger });

    timelines.set(el, tl);
  }

  function playEl(el) {
    if (finalized.has(el)) return;
    if (once && el.__aniSplitPlayedOnce) return;
    if (!once && isOnceElement(el) && el.__aniSplitPlayedOnce) return;

    if (!prefersReduced) {
      if (!timelines.has(el)) buildFor(el);
      const tl = timelines.get(el);
      if (!tl) return;

      
      const d = parseFloat(el.dataset?.delay ?? '');
      if (!Number.isNaN(d)) tl.delay(Math.max(0, d * runtimeDelayScale));

      if (isOnceElement(el)) el.__aniSplitPlayedOnce = true;
      tl.play(0);
    }
    if (prefersReduced && isOnceElement(el)) el.__aniSplitPlayedOnce = true;
  }

  function deactivateEl(el) {
    if (isOnceElement(el)) return;
    const tl = timelines.get(el);
    if (!tl) return;
    if (resetOnExit) tl.pause(0);
    else if (reverseOnExit) tl.reverse();
  }

  function playAll(root, filter = null) {
    const sel = filter ? `${targetSelector}${filter}` : targetSelector;
    root.querySelectorAll(sel).forEach(playEl);
  }

  function deactivateAll(root, filter = null) {
    const sel = filter ? `${targetSelector}${filter}` : targetSelector;
    root.querySelectorAll(sel).forEach(deactivateEl);
  }

  
  const onInviewEnter = (e) => {
    const scope = e.target;
    if (!(scope instanceof Element)) return;
    playAll(scope, '.ani-when-inview');
  };

  const onInviewExit = (e) => {
    const scope = e.target;
    if (!(scope instanceof Element)) return;
    deactivateAll(scope, '.ani-when-inview');
  };

  document.addEventListener('ani:inview-enter', onInviewEnter);
  document.addEventListener('ani:inview-exit',  onInviewExit);
  disposers.push(() => {
    document.removeEventListener('ani:inview-enter', onInviewEnter);
    document.removeEventListener('ani:inview-exit',  onInviewExit);
  });

  
  function wireHoverSelf(root = document) {
    const targets = root.querySelectorAll(`${targetSelector}.ani-when-hover`);
    targets.forEach((el) => {
      const over = () => playEl(el);
      const out  = () => deactivateEl(el);
      el.addEventListener('mouseenter', over);
      el.addEventListener('mouseleave', out);
      
      const mo = new MutationObserver(() => {
        if (el.classList.contains('is-hover')) playEl(el);
        else deactivateEl(el);
      });
      mo.observe(el, { attributes: true, attributeFilter: ['class'] });
      
      if (!prefersReduced) buildFor(el);

      disposers.push(() => {
        el.removeEventListener('mouseenter', over);
        el.removeEventListener('mouseleave', out);
        try { mo.disconnect(); } catch {}
      });
    });
  }
  wireHoverSelf();

  
  function wireFocusSelf(root = document) {
    const targets = root.querySelectorAll(`${targetSelector}.ani-when-focus`);
    targets.forEach((el) => {
      const onFocus = () => playEl(el);
      const onBlur  = () => deactivateEl(el);
      el.addEventListener('focus', onFocus, true);
      el.addEventListener('blur',  onBlur,  true);
      const mo = new MutationObserver(() => {
        if (el.classList.contains('is-focus')) playEl(el);
        else deactivateEl(el);
      });
      mo.observe(el, { attributes: true, attributeFilter: ['class'] });
      if (!prefersReduced) buildFor(el);

      disposers.push(() => {
        el.removeEventListener('focus', onFocus, true);
        el.removeEventListener('blur',  onBlur,  true);
        try { mo.disconnect(); } catch {}
      });
    });
  }
  wireFocusSelf();

  
  function wireSelected(root = document) {
    
    const inputs = root.querySelectorAll(`${targetSelector}.ani-when-selected,input${targetSelector}.ani-when-selected`);
    inputs.forEach((el) => {
      const handle = () => {
        
        if ('checked' in el) {
          if (el.checked) playEl(el);
          else deactivateEl(el);
        } else {
          
          if (el.classList.contains('is-selected')) playEl(el);
          else deactivateEl(el);
        }
      };
      el.addEventListener('change', handle);
      const mo = new MutationObserver(handle);
      mo.observe(el, { attributes: true, attributeFilter: ['class'] });
      if (!prefersReduced) buildFor(el);
      
      handle();

      disposers.push(() => {
        el.removeEventListener('change', handle);
        try { mo.disconnect(); } catch {}
      });
    });
  }
  wireSelected();

  
  function wireAttention(root = document) {
    const targets = root.querySelectorAll(`${targetSelector}.ani-when-attention`);
    targets.forEach((el) => {
      const handle = () => {
        if (el.classList.contains('is-attention')) playEl(el);
        else deactivateEl(el);
      };
      const mo = new MutationObserver(handle);
      mo.observe(el, { attributes: true, attributeFilter: ['class'] });
      if (!prefersReduced) buildFor(el);
      
      handle();

      disposers.push(() => {
        try { mo.disconnect(); } catch {}
      });
    });
  }
  wireAttention();

  
  function wireParentHover(root = document) {
    const scopes = root.querySelectorAll('.ani-hover-scope');
    scopes.forEach((scope) => {
      const over = () => playAll(scope, ' .ani-when-parent-hover' + targetSelector.replace(/^\./, '.')); 
      const out  = () => deactivateAll(scope, ' .ani-when-parent-hover' + targetSelector.replace(/^\./, '.'));
      scope.addEventListener('mouseenter', over);
      scope.addEventListener('mouseleave', out);
      const mo = new MutationObserver(() => {
        const active = scope.classList.contains('is-hover') || scope.matches(':focus-within') || scope.classList.contains('is-focus');
        if (active) over(); else out();
      });
      mo.observe(scope, { attributes: true, attributeFilter: ['class'] });
      
      if (!prefersReduced) scope.querySelectorAll(`${targetSelector}.ani-when-parent-hover`).forEach(buildFor);

      disposers.push(() => {
        scope.removeEventListener('mouseenter', over);
        scope.removeEventListener('mouseleave', out);
        try { mo.disconnect(); } catch {}
      });
    });
  }
  wireParentHover();

  
  function wireSectionDominant(root = document) {
    const sections = root.querySelectorAll('[data-ani-section]');
    sections.forEach((sec) => {
      const act = () => {
        if (sec.classList.contains('is-dominant')) {
          
          sec.querySelectorAll(`${targetSelector}.ani-when-section-dominant, ${targetSelector}[data-ani-dominant-accent]`).forEach(playEl);
        } else {
          sec.querySelectorAll(`${targetSelector}.ani-when-section-dominant, ${targetSelector}[data-ani-dominant-accent]`).forEach(deactivateEl);
        }
      };
      const mo = new MutationObserver(act);
      mo.observe(sec, { attributes: true, attributeFilter: ['class'] });
      if (!prefersReduced) sec.querySelectorAll(`${targetSelector}.ani-when-section-dominant, ${targetSelector}[data-ani-dominant-accent]`).forEach(buildFor);
      
      act();

      disposers.push(() => {
        try { mo.disconnect(); } catch {}
      });
    });
  }
  wireSectionDominant();

  
  return () => {
    disposers.splice(0).forEach((fn) => { try { fn?.(); } catch {} });

    splitTargets.forEach((el) => {
      const tl = timelines.get(el);
      if (tl) {
        try { tl.kill(); } catch {}
        timelines.delete(el);
      }
      const split = splits.get(el);
      if (split) {
        try { split.revert(); } catch {}
        splits.delete(el);
      }
      splitItems.delete(el);
    });
    splitTargets.clear();
    reservedHosts.forEach((host) => {
      const data = hostReservations.get(host);
      if (!data) return;
      host.style.minHeight = data.minHeight;
      hostReservations.delete(host);
    });
    reservedHosts.clear();
  };
}

export default initAniSplitStagger;
